/**
 * RuleSetSupersedeModal — Publish a new version superseding an existing RuleSet.
 *
 * Pre-fills fields from the old RuleSet so the architect only needs to
 * update what changed (new ID, new effective date, updated description, etc.).
 *
 * DRR-096: Version chain — old.superseded_by = new.ruleset_id.
 * DRR-112: Backend dual-writes Policy primitive for the new version.
 */

import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  Switch,
  Button,
  Group,
  Stack,
  Paper,
  Text,
  Badge,
  Alert,
} from '@mantine/core';
import { IconAlertCircle, IconArrowRight } from '@tabler/icons-react';
import { useSupersedeRuleSetMutation } from '@/generated/graphql';
import type { ListRuleSetsQuery } from '@/generated/graphql';

type RuleSetItem = NonNullable<ListRuleSetsQuery['ruleSets']>[number];

// ============================================================================
// TYPES
// ============================================================================

interface SupersedeFormState {
  newRulesetId: string;
  name: string;
  displayName: string;
  description: string;
  safetyLevel: string;
  isLifeCritical: boolean;
  version: string;
  effectiveDate: string;
  jurisdiction: string;
  category: string;
  setting: string;
  appliesToIndustries: string[];
  complianceFrameworks: string[];
}

// ============================================================================
// COMPONENT
// ============================================================================

interface RuleSetSupersedeModalProps {
  opened: boolean;
  onClose: () => void;
  onSuperseded: () => void;
  /** The RuleSet being superseded */
  oldRuleSet: RuleSetItem | null;
}

export function RuleSetSupersedeModal({
  opened,
  onClose,
  onSuperseded,
  oldRuleSet,
}: RuleSetSupersedeModalProps) {
  const [form, setForm] = useState<SupersedeFormState>({
    newRulesetId: '',
    name: '',
    displayName: '',
    description: '',
    safetyLevel: 'standard',
    isLifeCritical: false,
    version: '2.0',
    effectiveDate: '',
    jurisdiction: '',
    category: 'compliance',
    setting: 'general',
    appliesToIndustries: [],
    complianceFrameworks: [],
  });
  const [supersedeError, setSupersedeError] = useState<string | null>(null);

  const [supersedeRuleSet, { loading }] = useSupersedeRuleSetMutation();

  // Pre-fill from old ruleset when it changes
  useEffect(() => {
    if (!oldRuleSet) return;
    setForm({
      newRulesetId: '',
      name: oldRuleSet.displayName,
      displayName: oldRuleSet.displayName,
      description: oldRuleSet.description,
      safetyLevel: oldRuleSet.safetyLevel,
      isLifeCritical: oldRuleSet.isLifeCritical,
      version: '2.0',
      effectiveDate: '',
      jurisdiction: oldRuleSet.jurisdiction,
      category: oldRuleSet.category,
      setting: oldRuleSet.setting,
      appliesToIndustries: [...oldRuleSet.appliesToIndustries],
      complianceFrameworks: [...oldRuleSet.complianceFrameworks],
    });
    setSupersedeError(null);
  }, [oldRuleSet]);

  function set<K extends keyof SupersedeFormState>(field: K, value: SupersedeFormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleClose() {
    setSupersedeError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!oldRuleSet) return;
    setSupersedeError(null);
    try {
      await supersedeRuleSet({
        variables: {
          oldId: oldRuleSet.rulesetId,
          input: {
            rulesetId: form.newRulesetId,
            name: form.name,
            displayName: form.displayName,
            description: form.description,
            safetyLevel: form.safetyLevel,
            isLifeCritical: form.isLifeCritical,
            jurisdiction: form.jurisdiction,
            category: form.category,
            setting: form.setting,
            appliesToIndustries: form.appliesToIndustries,
            version: form.version || null,
            effectiveDate: form.effectiveDate || null,
            complianceFrameworks: form.complianceFrameworks,
            ruleIds: [],
            affectsWorkflows: [],
            sourceLegislation: null,
          },
        },
      });
      onSuperseded();
      handleClose();
    } catch (err) {
      setSupersedeError(String(err));
    }
  }

  const valid = form.newRulesetId.trim() !== '' && form.effectiveDate.trim() !== '';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Supersede RuleSet — Publish New Version"
      size="lg"
    >
      {oldRuleSet && (
        <Paper withBorder p="sm" mb="md" bg="gray.0">
          <Group gap="xs" mb={4}>
            <Text size="xs" c="dimmed">Superseding:</Text>
            <Badge color="gray" size="xs">{oldRuleSet.rulesetId}</Badge>
            <IconArrowRight size={12} />
            <Badge color="blue" size="xs">New version</Badge>
          </Group>
          <Text size="sm" fw={500}>{oldRuleSet.displayName}</Text>
          <Text size="xs" c="dimmed">{oldRuleSet.jurisdiction}</Text>
        </Paper>
      )}

      <Stack gap="sm">
        <TextInput
          label="New RuleSet ID"
          placeholder="rs-4-1-006"
          description="New scope-encoded ID for this version (DRR-089)"
          required
          value={form.newRulesetId}
          onChange={(e) => set('newRulesetId', e.target.value)}
        />
        <Group grow>
          <TextInput
            label="Version"
            placeholder="2.0"
            value={form.version}
            onChange={(e) => set('version', e.target.value)}
          />
          <TextInput
            label="Effective Date"
            placeholder="2026-01-01"
            description="When this version takes effect (DRR-096)"
            required
            value={form.effectiveDate}
            onChange={(e) => set('effectiveDate', e.target.value)}
          />
        </Group>
        <TextInput
          label="Display Name"
          required
          value={form.displayName}
          onChange={(e) => set('displayName', e.target.value)}
        />
        <Textarea
          label="Description"
          description="Describe what changed in this version"
          rows={3}
          value={form.description}
          onChange={(e) => set('description', e.target.value)}
        />
        <Group grow>
          <TextInput
            label="Jurisdiction"
            value={form.jurisdiction}
            onChange={(e) => set('jurisdiction', e.target.value)}
          />
          <Select
            label="Safety Level"
            data={['standard', 'high', 'critical']}
            value={form.safetyLevel}
            onChange={(v) => set('safetyLevel', v ?? 'standard')}
          />
        </Group>
        <MultiSelect
          label="Applies to Industries"
          data={['assisted_living', 'healthcare', 'home_care', 'hospice', 'financial', 'legal', 'payroll']}
          value={form.appliesToIndustries}
          onChange={(v) => set('appliesToIndustries', v)}
        />
        <Switch
          label="Life-Critical RuleSet"
          checked={form.isLifeCritical}
          onChange={(e) => set('isLifeCritical', e.target.checked)}
        />

        {supersedeError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {supersedeError}
          </Alert>
        )}
      </Stack>

      <Group justify="space-between" mt="xl">
        <Button variant="subtle" onClick={handleClose}>
          Cancel
        </Button>
        <Button loading={loading} disabled={!valid} onClick={handleSubmit} color="orange">
          Publish New Version
        </Button>
      </Group>
    </Modal>
  );
}
